import { INumberInputSchema } from '../../../../../types'
import { memo, useEffect, useState } from 'react'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import { selectRuleConfigBit, selectRuleIsChecked } from '../../../../../../../store/selectors/ruleCardSelectors'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { useFormValidation } from '../../../../../../../hooks/useFormValidation'
import { inputValidationCheck } from '../../../../../../../validators/inputValidators/inputValidators'
import { getLabel } from '../../../../../../../utils/getLabel'
import { initInputData } from '../../../../../../../utils/InitInputData'
import { inputDataDispatcher } from '../../../../../../../utils/InputDataDispatcher'
import {
    buildControlsExt,
    integerInput,
} from '../../../../../../inputs/controls'
import { integerInputErrors } from '../../../../../../../utils/validationErrors/integers/integerInputErrors'

export interface Props {
    dataPath: string
    pathWithElementID: string | undefined
    dataSchema: INumberInputSchema
    name: string
    wrapper?: string
}

export const RuleInt = memo(function RuleInt({
    dataPath,
    dataSchema,
    name,
    wrapper,
    pathWithElementID,
}: Props) {
    const dispatch = useAppDispatch()
    const pathSplit = dataPath.split(PATH_JOINER)
    const configBit = useAppSelector(selectRuleConfigBit(pathSplit))
    const path = [...pathSplit, name]
    const [checkPath, setCheckPath] = useState<string[]>([])
    const isChecked = useAppSelector(selectRuleIsChecked)
    const {
        is_required: isRequired,
        name: inputLabel,
        type,
        description: tooltipText,
        default: defaultValue,
        change_warning: changeWarning,
    } = dataSchema
    const validationCheck = inputValidationCheck(
        type,
        dataSchema,
        configBit?.[name]
    )
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit || {}, { [name]: validationCheck })
    const label= getLabel(name, inputLabel)
    const className = !wrapper ? 'w-100' : ''
    const errorKey= pathWithElementID
        ? [...pathWithElementID.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const isDataEmpty = Object.keys(configBit || {}).length
    const isUndefined = typeof configBit?.[name] === 'undefined'
    const isEmpty= !configBit || !isDataEmpty || isUndefined

    useEffect(() => {
        if (isChecked && dataPath.length) {
            isValid()
        }
    }, [isChecked, dataPath.length]) // eslint-disable-line

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit, dataPath.length])

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            initInputData(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    isRequired,
                    errorKey,
                    data: configBit,
                    setInputState,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            inputDataDispatcher(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    data: configBit,
                    inputState,
                    validationCheck,
                    errorKey,
                    isUndefined,
                    checkPath,
                    type,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, [touched[name], isChecked]) // eslint-disable-line

    const handleOnBlur = () => {
        inputDataDispatcher(
            {
                isEmpty,
                data: configBit,
                name,
                path,
                dataSchema,
                inputState,
                validationCheck,
                errorKey,
                isUndefined,
                checkPath,
                type,
                isRuleCard: true,
            },
            dispatch
        )
        setTouched((prev: any) => ({ ...prev, [name]: true }))
    }

    const controlDescriptors = [
        integerInput({
            name,
            tooltipText,
            changeWarning,
            label,
            validationType: type,
            className,
            isColumn: true,
            errMsg: integerInputErrors(inputState?.[name], dataSchema),
            isRequired,
            placeholder: defaultValue,
            onBlur: handleOnBlur,
        }),
    ]

    const IntegerInput = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    return <div className={`${!wrapper ? 'w-100' : ''}`}>{IntegerInput}</div>
})
